<script setup lang="ts">
import { computed, ref } from 'vue';
import { useCharacterStore } from '~/stores/character';
import { useInviteStore } from '~/stores/invite';
import { useUserStore } from '~/stores/user';
import { defaultCharacter } from '~/models/character';

// Props for the navigation item
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

// Check if the item has children
const hasChildren = computed(() => props.item.children && props.item.children.length > 0);

// Get current route to determine if we should auto-expand
const route = useRoute();
const characterStore = useCharacterStore();
const userStore = useUserStore();

// Auto-expand first level for all routes
const shouldAutoExpand = computed(() => {
  return props.item.path === '/galactic-guide-to-the-universe' ||
         props.item.path === '/gmg' ||
         props.item.path === '/nucleus' ||
         props.item.path === '/nucleus/character';
});

// Check if this item is both expandable and navigable (e.g. Character Editor with character children)
const isNavigableParent = computed(() => hasChildren.value && !!(props.item.redirect || props.item.to));
const parentNavPath = computed(() => props.item.redirect || props.item.to || props.item.path);

// Check if this is a "new character" shortcut
const isNewCharacter = computed(() => !!props.item.isNewCharacter);

// Check if this is a character nav item
const isCharacterItem = computed(() => !!props.item.characterId);
const isSharedCharacter = computed(() => !!props.item.isShared);
const isTrashItem = computed(() => !!props.item.isTrash);
const isOwnedCharacter = computed(() => isCharacterItem.value && !isSharedCharacter.value && !isTrashItem.value);

const isActiveCharacter = computed(() => {
  return isCharacterItem.value && characterStore.character.id === props.item.characterId;
});

// Share dialog state
const showShareDialog = ref(false);

const onCharacterClick = async () => {
  if (isSharedCharacter.value && props.item.shareId) {
    const charData = await characterStore.loadSharedCharacter(props.item.shareId);
    if (charData) {
      characterStore.select(charData);
    }
  } else {
    const char = characterStore.characterById(props.item.characterId);
    if (char) {
      characterStore.select(char);
    }
  }
  await navigateTo('/nucleus/CharacterEditor');
};

const onDeleteCharacter = async () => {
  if (isSharedCharacter.value && props.item.shareId) {
    // Recipient removing a share
    const inviteStore = useInviteStore();
    await inviteStore.removeCharacterShare(props.item.shareId);
    await characterStore.fetchSharedCharacters();
  } else {
    // Owner deleting — goes to trash via Cloud Function
    const char = characterStore.characterById(props.item.characterId);
    if (char) {
      await characterStore.delete(char);
    }
  }
};

const onCopyCharacter = async () => {
  if (isSharedCharacter.value && props.item.shareId) {
    // Load shared character via Cloud Function, then copy
    const charData = await characterStore.loadSharedCharacter(props.item.shareId);
    if (charData) {
      const copy = JSON.parse(JSON.stringify(charData));
      copy.id = '-1';
      copy.name = `${copy.name} (Copy)`;
      copy.lastSaved = undefined;
      characterStore.add(copy);
      characterStore.select(copy);
      await characterStore.upsert(copy);
      await navigateTo('/nucleus/CharacterEditor');
    }
  } else {
    const char = characterStore.characterById(props.item.characterId);
    if (char) {
      const copy = JSON.parse(JSON.stringify(char));
      copy.id = '-1';
      copy.name = `${copy.name} (Copy)`;
      copy.lastSaved = undefined;
      characterStore.add(copy);
      characterStore.select(copy);
      await characterStore.upsert(copy);
      await navigateTo('/nucleus/CharacterEditor');
    }
  }
};

const onRestoreFromTrash = async () => {
  if (props.item.trashId) {
    await characterStore.restoreFromTrash(props.item.trashId);
    await navigateTo('/nucleus/CharacterEditor');
  }
};

const onShareCharacter = () => {
  showShareDialog.value = true;
};

const onNewCharacter = async () => {
  characterStore.select(defaultCharacter());
  await navigateTo('/nucleus/CharacterEditor');
};
</script>

<template>
  <!-- For items with children, use q-expansion-item -->
  <q-expansion-item
    v-if="hasChildren"
    :label="isNavigableParent ? undefined : item.title"
    class="non-leaf-node scifi-font-med"
    :model-value="shouldAutoExpand"
    :default-opened="shouldAutoExpand"
  >
    <template v-if="isNavigableParent" #header>
      <q-item-section>
        <NuxtLink :to="parentNavPath" class="expansion-nav-link">{{ item.title }}</NuxtLink>
      </q-item-section>
    </template>
    <ul>
      <NavigationItem v-for="(child, index) in item.children" :key="index" :item="child" />
    </ul>
  </q-expansion-item>

  <!-- Trash nav items -->
  <li v-else-if="isTrashItem" class="character-item">
    <span class="leaf-node character-link trash-char">
      <q-icon name="delete_outline" size="xs" color="orange" class="q-mr-xs" />
      <span class="char-name">{{ item.title }}</span>
    </span>
    <div class="char-actions">
      <ActionIcon
        :item="{}"
        :isFlat="true"
        :action="onRestoreFromTrash"
        icon="restore"
        color="green"
        tooltipTitle="RESTORE"
        tooltipDesc="Restore from Disintegrator Bin"
        class="action-btn"
      />
    </div>
  </li>

  <!-- Character nav items -->
  <li v-else-if="isCharacterItem" class="character-item">
    <a href="#" class="leaf-node character-link" :class="{ 'active-leaf-node': isActiveCharacter, 'shared-char': isSharedCharacter }"
      @click.prevent="onCharacterClick">
      <q-icon v-if="isSharedCharacter" name="people" size="xs" color="blue" class="q-mr-xs" />
      <span class="char-name">{{ item.title }}</span>
      <span v-if="item.lastSaved" class="char-date">{{ item.lastSaved }}</span>
    </a>
    <div class="char-actions">
      <ActionIcon
        :item="{}"
        :isFlat="true"
        :action="onCopyCharacter"
        icon="content_copy"
        color="blue"
        tooltipTitle="COPY CHARACTER"
        tooltipDesc="Create your own copy"
        class="action-btn"
      />
      <ActionIcon
        v-if="isOwnedCharacter"
        :item="{}"
        :isFlat="true"
        :action="onShareCharacter"
        icon="share"
        color="green"
        tooltipTitle="SHARE CHARACTER"
        tooltipDesc="Share read-only access"
        class="action-btn"
      />
      <ActionIcon
        v-if="isSharedCharacter"
        :item="{}"
        :isFlat="true"
        :action="onDeleteCharacter"
        icon="link_off"
        iconConfirm="check"
        color="orange"
        confirmColor="red"
        tooltipTitle="UNLINK SHARE"
        tooltipDesc="Remove shared access"
        class="action-btn"
      />
      <ActionIcon
        v-if="isOwnedCharacter"
        :item="{}"
        :isFlat="true"
        :action="onDeleteCharacter"
        icon="delete"
        iconConfirm="check"
        color="red"
        confirmColor="red"
        tooltipTitle="DISINTEGRATE"
        tooltipDesc="Move to Disintegrator Bin"
        class="action-btn"
      />
    </div>
    <DialogShare
      v-if="isOwnedCharacter"
      v-model="showShareDialog"
      :characterId="item.characterId"
      :characterName="item.title"
    />
  </li>

  <!-- New character shortcut -->
  <li v-else-if="isNewCharacter">
    <a href="#" class="leaf-node new-character-link" @click.prevent="onNewCharacter">
      {{ item.title }}
    </a>
  </li>

  <!-- For items without children, render as a NuxtLink -->
  <li v-else>
    <NuxtLink
      :to="item.path"
      active-class="active-leaf-node"
      exact-active-class="active-leaf-node"
      class="leaf-node">{{ item.title }}
    </NuxtLink>
  </li>
</template>

<style scoped>
a {
  text-decoration: none;
}

.leaf-node {
  /* heading-sub = cyan in default / mint in NERD / blueprint in CORSEC etc.
     `--cc-link` is the body-prose link colour (purple in default) and
     gives the wrong feel in the sidebar. */
  color: var(--cc-heading-sub, #00ffea);
  font-family: var(--cc-font-mono, 'Share Tech Mono', 'Courier New', monospace);
  font-size: 0.95em;
  display: block;
  padding: 4px 8px;
  border-left: 2px solid transparent;
  transition: all 0.15s;
}

.leaf-node:hover {
  color: var(--cc-accent, #ff6600);
  background: rgba(255, 255, 255, 0.04);
  border-left-color: var(--cc-accent, #00ffea40);
}

li {
  list-style-type: none;
  margin-bottom: 2px;
}

li:last-child {
  margin-bottom: 0;
}

.non-leaf-node {
  /* warn-border = amber/orange across every theme — gives section
     headers a consistent "attention" colour that contrasts with the
     leaf-node's heading-sub colour in every palette. */
  color: var(--cc-warn-border, #ff6600);
  font-family: var(--cc-font-mono, 'Share Tech Mono', 'Courier New', monospace);
  font-size: 1em;
  letter-spacing: 1px;
}

:deep(.non-leaf-node .q-item__label) {
  font-family: var(--cc-font-mono, 'Share Tech Mono', 'Courier New', monospace);
  font-size: 1em;
  color: var(--cc-warn-border, #ff6600);
}

:deep(.non-leaf-node .q-expansion-item__toggle-icon) {
  color: var(--cc-heading-sub, #00ffea60);
}

.active-leaf-node {
  /* Lime "you are here" is a Cybercore chrome identity colour —
     anchored across all themes against the dark sidebar background. */
  color: #51ff00;
  border-left-color: #51ff00;
  background: rgba(81, 255, 0, 0.08);
}

/* Character items */
.character-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
  border-bottom: 1px solid var(--cc-table-border, #00ffea08);
}

.character-link {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
  padding: 4px 8px;
  font-family: var(--cc-font-mono, 'Share Tech Mono', 'Courier New', monospace);
  font-size: 0.9em;
  color: var(--cc-heading-sub, #00ffea);
  border-left: 2px solid transparent;
  transition: all 0.15s;
}

.character-link:hover {
  color: var(--cc-accent, #ff6600);
  background: rgba(255, 255, 255, 0.04);
  border-left-color: var(--cc-accent, #00ffea40);
}

.active-leaf-node.character-link {
  color: #51ff00;
  border-left-color: #51ff00;
}

.char-name {
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.char-date {
  flex-shrink: 0;
  font-size: 0.65em;
  color: var(--cc-heading-sub, #00ffea60);
}

.char-actions {
  display: flex;
  gap: 0;
  flex-shrink: 0;
  overflow: hidden;
}

.character-item .action-btn {
  opacity: 0.3;
}

.character-item:hover .action-btn {
  opacity: 1;
}

.shared-char {
  color: var(--cc-example-border, #4db8ff);
}

.trash-char {
  color: var(--cc-story-text, #666);
  opacity: 0.7;
}

.new-character-link {
  color: var(--cc-accent, #51ff00) !important;
  font-size: 0.9em !important;
}

.expansion-nav-link {
  color: var(--cc-heading, #ff6600);
  text-decoration: none;
  font-family: var(--cc-font-mono, 'Share Tech Mono', 'Courier New', monospace);
  font-size: 1em;
  letter-spacing: 1px;
  transition: color 0.15s;
}

.expansion-nav-link:hover {
  color: var(--cc-link, #00ffea);
}
</style>
